import React, { useState } from 'react';

import { useIntl } from 'react-intl';

import { IDeliveryAddress } from 'generated/rbi-graphql';
import { useMediaQuery } from 'hooks/use-media-query';
import { useScrollTop } from 'hooks/use-scroll-top';
import { useDeleteSavedAddress } from 'pages/store-locator/new-ui/edit-address/use-delete-saved-address';
import { StoreLocatorSteps } from 'pages/store-locator/types';

import { IDeliveryAddressLocation } from '../save-delivery-address/types';

import { DeliveryAddressesList } from './delivery-addresses-list';

interface ISavedAddressesProps {
  addresses: IDeliveryAddress[];
  setAddress: (address: IDeliveryAddress) => void;
  initialCollapsed?: boolean;
  currentStep: StoreLocatorSteps;
  setCurrentStep: (step: StoreLocatorSteps) => void;
  setDeliveryAddress: (deliveryAddress: IDeliveryAddressLocation | undefined) => void;
  onEditAddress?: (address: IDeliveryAddress) => void;
}

export const SavedAddresses: React.FC<ISavedAddressesProps> = ({
  addresses,
  setAddress,
  initialCollapsed = true,
  currentStep,
  setCurrentStep,
  setDeliveryAddress,
  onEditAddress,
}) => {
  const isMobile = useMediaQuery('headerMobile');

  const { formatMessage } = useIntl();
  const isSavedAddressesStep = currentStep === StoreLocatorSteps.SAVED_ADDRESS;
  const [collapsed, setCollapsed] = useState(initialCollapsed || !isSavedAddressesStep);
  useScrollTop();

  const { RemoveSavedAddressModal, openRemoveSavedAddressModal, removeSavedAddressModalBody } =
    useDeleteSavedAddress({
      setCurrentStep,
      setDeliveryAddress,
    });

  const handleTitleClick = () => {
    if (isMobile) {
      setCurrentStep(StoreLocatorSteps.SAVED_ADDRESS);
      setCollapsed(false);
    } else {
      setCollapsed(!collapsed);
    }
  };

  if (addresses && addresses.length !== 0) {
    return (
      <>
        <DeliveryAddressesList
          label={formatMessage({ id: 'savedAddresses' })}
          addresses={addresses}
          onAddressSelect={setAddress}
          collapsed={collapsed}
          collapsible={!isSavedAddressesStep}
          onTitleClick={handleTitleClick}
          showTitle={!isSavedAddressesStep}
          onAddressDelete={openRemoveSavedAddressModal}
          showDelete={true}
          onEditAddress={onEditAddress}
        />

        <RemoveSavedAddressModal
          data-testid="remove-saved-address-modal"
          buttonLabel={formatMessage({ id: 'remove' })}
          heading={formatMessage({ id: 'removeSavedAddressTitleAlt' })}
          body={removeSavedAddressModalBody}
          showCloseButton
        />
      </>
    );
  }
  return null;
};
